import JSZip from "jszip";
import { saveAs } from "file-saver";

// Function to get default content for files that might be empty
const getDefaultContentForFile = (fileName) => {
  if (fileName.endsWith('.gitkeep')) {
    return '# This file ensures the directory is tracked by git\n# Remove this file if you add actual files to this directory\n';
  }
  if (fileName.endsWith('app.log')) {
    return '# Application logs will be written here\n# This file is created to ensure the logs directory exists\n';
  }
  if (fileName.endsWith('.log')) {
    return '# Log file\n';
  }
  return '# Empty file\n';
};

// Download Playwright TypeScript project as a zip folder
export const downloadPlaywrightTypeScriptProject = (projectData, setZipBlob = null) => {
  try {
    console.log("downloadPlaywrightTypeScriptProject called with:", projectData);
    console.log("projectData type:", typeof projectData);
    console.log("projectData is array:", Array.isArray(projectData));
    
    const zip = new JSZip();

    // Handle the array format returned by the backend
    // projectData is an array of objects where each object has a file name as key and content as value
    if (Array.isArray(projectData)) {
      console.log("Processing array with", projectData.length, "items");
      
      // Log all file names for debugging
      const allFileNames = projectData.map(fileObj => Object.keys(fileObj)[0]);
      console.log("📁 All files being processed:", allFileNames);
      
      // Debug: Check specifically for page objects
      const pageObjectFiles = allFileNames.filter(name => name.startsWith("src/pages/"));
      console.log("🔍 Page object files found:", pageObjectFiles);
      console.log("🔍 Page object files count:", pageObjectFiles.length);
      
      let filesAddedToZip = 0;
      let filesSkipped = 0;
      let pageObjectsAdded = 0;
      
      projectData.forEach((fileObj, index) => {
        console.log(`File object ${index}:`, fileObj);
        // Each fileObj is like { "fileName": "content" }
        const fileName = Object.keys(fileObj)[0];
        const content = fileObj[fileName];
        
        console.log(`Processing file: ${fileName}, content length: ${content ? content.length : 0}`);
        
        let finalContent = content;
        
        if (fileName && content) {
          console.log(`✅ Processing file: ${fileName} (content length: ${content.length})`);
          finalContent = content;
        } else if (fileName && !content) {
          // Handle files with empty content (like .gitkeep files, empty log files, etc.)
          console.log(`⚠️ Processing file with empty content: ${fileName}`);
          const defaultContent = getDefaultContentForFile(fileName);
          console.log(`📝 Using default content for ${fileName}: ${defaultContent}`);
          finalContent = defaultContent;
        } else {
          console.log(`❌ Skipping file: ${fileName} (content: ${content ? 'empty' : 'null'})`);
          filesSkipped++;
          return; // Skip this file
        }
        
        if (fileName && finalContent) {
          // Handle different file types and organize them in the zip structure
          if (fileName === "package.json") {
            zip.file("package.json", finalContent);
            console.log("Added package.json to zip");
            filesAddedToZip++;
          } else if (fileName === "playwright.config.ts") {
            zip.file("playwright.config.ts", finalContent);
            console.log("Added playwright.config.ts to zip");
            filesAddedToZip++;
          } else if (fileName === "tsconfig.json") {
            zip.file("tsconfig.json", finalContent);
            console.log("Added tsconfig.json to zip");
            filesAddedToZip++;
          } else if (fileName === "README.md") {
            zip.file("README.md", finalContent);
            console.log("Added README.md to zip");
            filesAddedToZip++;
          } else if (fileName.startsWith("src/pages/")) {
            // Page object files
            console.log(`🔍 Processing page object: ${fileName}`);
            console.log(`🔍 Page object content length: ${finalContent ? finalContent.length : 0}`);
            console.log(`🔍 Page object content preview: ${finalContent ? finalContent.substring(0, 100) : 'null'}`);
            
            const pagesFolder = zip.folder("src/pages");
            const relativePath = fileName.replace("src/pages/", "");
            pagesFolder.file(relativePath, finalContent);
            console.log(`✅ Added page object file: ${fileName} (content length: ${finalContent.length})`);
            filesAddedToZip++;
            pageObjectsAdded++;
          } else if (fileName.startsWith("tests/specs/")) {
            // Test files (correct path according to project structure)
            const testsFolder = zip.folder("tests/specs");
            const relativePath = fileName.replace("tests/specs/", "");
            testsFolder.file(relativePath, finalContent);
            console.log(`Added test file: ${fileName}`);
          } else if (fileName.startsWith("src/tests/")) {
            // Legacy test files path
            const testsFolder = zip.folder("src/tests");
            const relativePath = fileName.replace("src/tests/", "");
            testsFolder.file(relativePath, finalContent);
            console.log(`Added test file: ${fileName}`);
          } else if (fileName.startsWith("src/utils/")) {
            // Utility files
            const utilsFolder = zip.folder("src/utils");
            const relativePath = fileName.replace("src/utils/", "");
            utilsFolder.file(relativePath, finalContent);
            console.log(`Added utility file: ${fileName}`);
          } else if (fileName.startsWith("src/pages/base/")) {
            // Base page files
            const baseFolder = zip.folder("src/pages/base");
            const relativePath = fileName.replace("src/pages/base/", "");
            baseFolder.file(relativePath, finalContent);
            console.log(`Added base page file: ${fileName}`);
          } else if (fileName.startsWith("src/pages/base.page.ts")) {
            // Base page file
            zip.file("src/pages/base.page.ts", finalContent);
            console.log(`Added base page file: ${fileName}`);
          } else if (fileName.startsWith("src/logger/")) {
            // Logger files
            const loggerFolder = zip.folder("src/logger");
            const relativePath = fileName.replace("src/logger/", "");
            loggerFolder.file(relativePath, finalContent);
            console.log(`Added logger file: ${fileName}`);
          } else if (fileName.startsWith("src/reporting/")) {
            // Reporting files
            const reportingFolder = zip.folder("src/reporting");
            const relativePath = fileName.replace("src/reporting/", "");
            reportingFolder.file(relativePath, finalContent);
            console.log(`Added reporting file: ${fileName}`);
          } else if (fileName.startsWith("test-base/")) {
            // Test base files
            const testBaseFolder = zip.folder("test-base");
            const relativePath = fileName.replace("test-base/", "");
            testBaseFolder.file(relativePath, finalContent);
            console.log(`Added test base file: ${fileName}`);
            filesAddedToZip++;
          } else if (fileName.startsWith("test-setup/")) {
            // Test setup files
            const testSetupFolder = zip.folder("test-setup");
            const relativePath = fileName.replace("test-setup/", "");
            testSetupFolder.file(relativePath, finalContent);
            console.log(`✅ Added test setup file: ${fileName} (content length: ${finalContent.length})`);
            filesAddedToZip++;
          } else if (fileName.startsWith("config/env/")) {
            // Environment-specific files (.env.qa, .env.staging, etc.)
            const configEnvFolder = zip.folder("config/env");
            const relativePath = fileName.replace("config/env/", "");
            configEnvFolder.file(relativePath, finalContent);
            console.log(`Added config env file: ${fileName}`);
            filesAddedToZip++;
          } else if (fileName === "config/env.ts") {
            // Main environment configuration file
            const configFolder = zip.folder("config");
            configFolder.file("env.ts", finalContent);
            console.log(`Added main env config file: ${fileName}`);
            filesAddedToZip++;
          } else if (fileName.startsWith("config/")) {
            // Other config files
            const configFolder = zip.folder("config");
            const relativePath = fileName.replace("config/", "");
            configFolder.file(relativePath, finalContent);
            console.log(`Added config file: ${fileName}`);
            filesAddedToZip++;
          } else if (fileName.startsWith("logs/")) {
            // Log files
            const logsFolder = zip.folder("logs");
            const relativePath = fileName.replace("logs/", "");
            logsFolder.file(relativePath, finalContent);
            console.log(`Added log file: ${fileName}`);
            filesAddedToZip++;
          } else if (fileName.startsWith("reports/")) {
            // Report files
            const reportsFolder = zip.folder("reports");
            const relativePath = fileName.replace("reports/", "");
            reportsFolder.file(relativePath, finalContent);
            console.log(`Added report file: ${fileName}`);
            filesAddedToZip++;
          } else if (fileName.startsWith("test-data/")) {
            // Test data files
            const testDataFolder = zip.folder("test-data");
            const relativePath = fileName.replace("test-data/", "");
            testDataFolder.file(relativePath, finalContent);
            console.log(`✅ Added test data file: ${fileName} (content length: ${finalContent.length})`);
            filesAddedToZip++;
          } else {
            // Default: add file to root
            zip.file(fileName, finalContent);
            console.log(`Added file to root: ${fileName}`);
            filesAddedToZip++;
          }
        } else {
          console.log(`❌ Skipping file: ${fileName} (finalContent: ${finalContent ? 'empty' : 'null'})`);
          filesSkipped++;
        }
      });
      
      // Log summary of what was included
      console.log("📦 Project structure summary:");
      console.log("📁 Total files received:", projectData.length);
      console.log("📁 Files added to zip:", filesAddedToZip);
      console.log("📁 Files skipped:", filesSkipped);
      console.log("📄 Page objects added to zip:", pageObjectsAdded);
      
      // Log page objects specifically
      console.log("📄 Page objects received:", pageObjectFiles);
      
      // Log test data files specifically
      const testDataFiles = allFileNames.filter(name => name.startsWith("test-data/"));
      console.log("📊 Test data files received:", testDataFiles);
      console.log("📄 Page objects count:", pageObjectFiles.length);
      console.log("📁 Root files:", allFileNames.filter(name => !name.includes('/')));
      console.log("📁 Config files:", allFileNames.filter(name => name.startsWith('config/')));
      console.log("📁 Source files:", allFileNames.filter(name => name.startsWith('src/')));
      console.log("📁 Test files:", allFileNames.filter(name => name.startsWith('tests/')));
      console.log("📁 Test base files:", allFileNames.filter(name => name.startsWith('test-base/')));
      console.log("📁 Log files:", allFileNames.filter(name => name.startsWith('logs/')));
      console.log("📁 Report files:", allFileNames.filter(name => name.startsWith('reports/')));
      
    } else {
      console.error("Project data is not in expected array format");
      return;
    }

    // Generate the zip file
    zip.generateAsync({ type: "blob" }).then((content) => {
      console.log("Generated zip file, size:", content.size, "bytes");
      
      // Store zip blob if callback provided (for GitHub push)
      if (setZipBlob) {
        setZipBlob(content);
      }
      
      // Download the zip file directly to system's default download folder
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const fileName = `playwright-typescript-project.zip`;
      saveAs(content, fileName);
      console.log(`Download initiated for ${fileName}`);
    }).catch((error) => {
      console.error("Error generating zip file:", error);
    });

  } catch (error) {
    console.error("Error in downloadPlaywrightTypeScriptProject:", error);
  }
};

